import { Tooltip } from "@client/components/utils/Tooltip";
import { TriangleDown } from "@client/icons/TriangleDown";
import { TriangleUp } from "@client/icons/TriangleUp";
import {
  DispatchContext,
  StateContext,
  useEvaluableControls,
} from "@client/pages/round/Round";
import { formatMoney } from "@client/utils/formatMoney";
import { useTheme } from "@client/utils/useTheme";
import { isUserRoundAdmin } from "@engine/round/accessors";
import { Funder, Proposal, Recommender } from "@engine/round/aggregate";
import { Evaluator } from "@lib/types";
import clsx from "clsx";
import sortBy from "lodash/sortBy";
import { useContext, useState } from "react";
import { EditableNumberField } from "./TrancheListEditor/EditableNumberField";
import { FrontendEvaluation } from "./useEvaluatorEvaluationsCache";

type MVFTableSortableFieldName =
  | "name"
  | "matchRate"
  | "targetOutsideDonations"
  | "expectedMatchingFraction";

type MVFTableSort = {
  field: MVFTableSortableFieldName;
  descending: boolean;
};
const defaultSorting: MVFTableSort = { field: "name", descending: false };

export function MatchingTable({
  proposals,
  evaluations,
  evaluator,
}: {
  proposals: Proposal[];
  evaluations: FrontendEvaluation[];
  evaluator: Recommender | Funder;
}) {
  const [sorting, setSorting] = useState<MVFTableSort>(defaultSorting);
  const sortedEvaluations = sortEvaluationsBy(proposals, evaluations, sorting);

  // Calculate the max target donation amount from the current proposals
  const maxTargetDonation = Math.max(
    1, // Ensure at least 1 to avoid division by zero
    ...proposals.map((p) => p.typedFields.targetOutsideDonations ?? 0),
  );

  return (
    <div className="mx-4 p-2">
      <h2>Matching Table</h2>

      <table className="text-xs border-separate border-spacing-0">
        <thead>
          <tr className="sticky top-0 dark:bg-[#181818] z-10">
            {[
              "name",
              "matchRate",
              "targetOutsideDonations",
              "expectedMatchingFraction",
            ].map((fieldName) => (
              <SortableFieldHeading
                key={fieldName}
                fieldName={fieldName as MVFTableSortableFieldName}
                defaultDescending={fieldName !== "name"}
                sorting={sorting}
                setSorting={setSorting}
                className="bg-stone-100 dark:bg-[#181818]"
                evaluator={evaluator}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedEvaluations.map((evaluation) => (
            <MVFTableRow
              key={`${evaluation.evaluableId}-row`}
              proposal={proposals.find((p) => p.id === evaluation.evaluableId)!}
              evaluation={evaluation}
              evaluator={evaluator}
              maxTargetDonation={maxTargetDonation}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Helper to get background HSL color for Match Rate
function getMatchRateColor(
  rate: number | undefined | null,
  isDarkMode: boolean,
): string {
  if (rate == null || isNaN(rate)) return "transparent";

  // Clamp rate to the range [0.25, 4] for interpolation
  const clampedRate = Math.max(0.25, Math.min(4, rate));

  // Map log2(rate) from [-2, 2] to difficulty [0, 1] (0=hardest, 1=easiest)
  const log2Rate = Math.log2(clampedRate); // Range [-2, 2]
  const difficulty = (log2Rate + 2) / 4; // Range [0, 1]

  // Map difficulty [0, 1] to Hue [280, 210] (Purple to Blue)
  const hue = 280 - difficulty * 70;

  // Map difficulty [0, 1] to Lightness based on theme
  const lightnessPercent = isDarkMode
    ? 25 + difficulty * 25 // Dark mode: 25% (hard) to 50% (easy)
    : 65 + difficulty * 25; // Light mode: 65% (hard) to 90% (easy)

  return `hsl(${hue}, 55%, ${lightnessPercent}%)`; // Slightly reduced saturation
}

// Helper to get background HSL color for Expected Matching Fraction
function getExpectedFractionColor(
  fraction: number | undefined | null,
  isDarkMode: boolean,
): string {
  if (fraction == null) return "transparent";

  // Map fraction from 0-1 to hue values (red=0 to green=120)
  const hue = Math.max(0, Math.min(1, fraction)) * 120;

  // Use HSL with fixed saturation and lightness based on isDarkMode
  const lightness = isDarkMode ? "30%" : "80%";
  return `hsl(${hue}, 40%, ${lightness})`;
}

// Helper to get grayscale background HSL color for Target Outside Donations
function getTargetDonationGrayscale(
  amount: number | undefined | null,
  isDarkMode: boolean,
  maxAmount: number,
): string {
  if (amount == null || isNaN(amount) || amount < 0) return "transparent";

  const minAmount = 0;

  // Clamp and normalize amount to [0, 1]
  const clampedAmount = Math.min(maxAmount, Math.max(minAmount, amount));
  const normalizedAmount =
    (clampedAmount - minAmount) / (maxAmount - minAmount);

  // Map normalized amount [0, 1] to lightness
  // Lighter gray for smaller amounts, darker for larger amounts in both themes
  const lightnessPercent = isDarkMode
    ? 55 - normalizedAmount * 35 // Dark mode: 55% (lightest) down to 20% (darkest)
    : 95 - normalizedAmount * 35; // Light mode: 95% (lightest) down to 60% (darkest)

  return `hsl(0, 0%, ${lightnessPercent}%)`; // Hue 0, Saturation 0 for gray
}

function MVFTableRow({
  proposal,
  evaluation,
  evaluator,
  maxTargetDonation,
}: {
  proposal: Proposal;
  evaluation: FrontendEvaluation;
  evaluator: Evaluator;
  maxTargetDonation: number;
}) {
  const dispatch = useContext(DispatchContext);
  const { round, actingAsUserId: userId } = useContext(StateContext);
  const evaluableControls = useEvaluableControls(evaluation.evaluableId);
  const { isDarkMode } = useTheme();

  async function handleExpectedMatchingFractionConfirm(
    expectedMatchingFraction: number,
  ) {
    void dispatch({
      type: "SET_EXPECTED_MATCHING_FRACTION",
      evaluatorId: evaluator.id,
      evaluableId: evaluation.evaluableId,
      expectedMatchingFraction,
    });
  }

  const userCanModifyEvaluation =
    isUserRoundAdmin(round, userId) || evaluator?.memberId === userId;

  const typedFields = proposal.typedFields;

  return (
    <tr
      className={clsx("relative group", {
        "bg-stone-300 dark:bg-stone-600": evaluableControls.isFocus,
      })}
      key={evaluation.evaluableId}
    >
      <td className="border border-gray-500 dark:border-opacity-30 max-w-[180px] text-ellipsis overflow-hidden whitespace-nowrap">
        {evaluation.evaluable.shortname}
      </td>
      <td
        className="border border-gray-500 dark:border-opacity-30 text-right"
        style={{
          backgroundColor:
            getMatchRateColor(typedFields.matchRate, isDarkMode) ?? undefined,
        }}
      >
        {typedFields.matchRate}x
      </td>
      <td
        className="border border-gray-500 dark:border-opacity-30 text-right"
        style={{
          backgroundColor:
            getTargetDonationGrayscale(
              typedFields.targetOutsideDonations,
              isDarkMode,
              maxTargetDonation,
            ) ?? undefined,
        }}
      >
        {typedFields.targetOutsideDonations !== undefined
          ? formatMoney(typedFields.targetOutsideDonations)
          : ""}
      </td>
      <td
        className="border border-gray-500 dark:border-opacity-30 text-right flex justify-end"
        style={{
          backgroundColor:
            getExpectedFractionColor(
              evaluation.expectedMatchingFraction,
              isDarkMode,
            ) ?? undefined,
        }}
      >
        <EditableNumberField
          value={evaluation.expectedMatchingFraction}
          isMoney={false}
          onSubmitted={handleExpectedMatchingFractionConfirm}
          min={0}
          max={1}
          disabled={!userCanModifyEvaluation}
          hasBorder={userCanModifyEvaluation}
        />
      </td>
    </tr>
  );
}

function SortableFieldHeading({
  fieldName,
  defaultDescending,
  sorting,
  setSorting,
  className,
  evaluator,
}: {
  fieldName: MVFTableSortableFieldName;
  defaultDescending: boolean;
  sorting: MVFTableSort;
  setSorting: (newSorting: MVFTableSort) => void;
  className?: string;
  evaluator: Recommender | Funder;
}) {
  function sortByThis() {
    if (sorting.field === fieldName) {
      setSorting({ field: fieldName, descending: !sorting.descending });
    } else {
      setSorting({ field: fieldName, descending: defaultDescending });
    }
  }

  const isSortedByThis = sorting.field === fieldName;

  const children = {
    name: "Name",
    matchRate: (
      <Tooltip
        title="Dollars S-process allocates for every external dollar"
        placement="top-start"
      >
        Match Rate
      </Tooltip>
    ),
    targetOutsideDonations: (
      <Tooltip
        title="Target amount of outside donations they'd like to have matched"
        placement="top-start"
      >
        Target Outside Donations
      </Tooltip>
    ),
    expectedMatchingFraction: (
      <Tooltip
        title={`${evaluator.shortname}'s Expectation of Matching Success (what % of their target outside donations ${evaluator.shortname} expects this proposal to raise)`}
        placement="top-start"
      >
        {`${evaluator.shortname}'s Expectation of Matching Success`}
      </Tooltip>
    ),
  }[fieldName];

  return (
    <td
      className={clsx(
        "cursor-pointer select-none border-b border-gray-500 dark:border-white border-opacity-50",
        "whitespace-normal max-w-24 align-bottom p-1",
        className,
      )}
      onClick={sortByThis}
    >
      {children}

      {isSortedByThis && (
        <>
          {sorting.descending ? (
            <TriangleDown className="w-4 h-4 dark:fill-white inline-block ml-1" />
          ) : (
            <TriangleUp className="w-4 h-4 dark:fill-white inline-block ml-1" />
          )}
        </>
      )}
      {!isSortedByThis && <span className="inline-block w-4 ml-1" />}
    </td>
  );
}

function sortEvaluationsBy(
  proposals: Proposal[],
  evaluations: FrontendEvaluation[],
  sorting: MVFTableSort,
) {
  let sorted = [...evaluations];
  sorted = sortBy(sorted, (e) => {
    const proposal = proposals.find((p) => p.id === e.evaluableId)!;
    return getFieldForSorting(proposal, e, sorting);
  });
  if (sorting.descending) {
    sorted.reverse();
  }
  return sorted;
}

function getFieldForSorting(
  proposal: Proposal,
  evaluation: FrontendEvaluation,
  sorting: MVFTableSort,
) {
  const typedFields = proposal.typedFields;
  switch (sorting.field) {
    case "name":
      return evaluation.evaluable.shortname;
    case "matchRate":
      return typedFields.matchRate;
    case "targetOutsideDonations":
      return typedFields.targetOutsideDonations;
    case "expectedMatchingFraction":
      return evaluation.expectedMatchingFraction;
  }
}
